import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, PackagePlus, AlertCircle, Minus, Plus } from 'lucide-react';
import { useApp } from '../AppProvider';
import { formatCurrency } from '../../lib/utils/formatters';
import { formatQuantity, type QuantityFormatOptions } from '../../lib/utils/quantityFormat';
import { resolveProductUnit } from '../../lib/constants/units';
import {
  applyDealOrderPlan,
  loadDealOrderLines,
  messaggioPiano,
  type DealLinkedOrder,
  type DealOrderLine,
  type DealOrderPlanResult,
} from '../../lib/services/dealOrderService';

/**
 * Divisione delle righe prodotto di un'offerta in uno o più ordini.
 *
 * Si sceglie in quanti ordini dividere, poi per ogni riga la quantità che va in
 * ciascun ordine (Ordine 1, Ordine 2...). Gli ordini ancora in bozza dell'offerta
 * compaiono come colonne già compilate e si possono ridividere; quelli confermati
 * o successivi non si toccano e la loro quantità non è più assegnabile.
 * Quanto non assegnato resta da ordinare. Gli ordini non possono essere più delle
 * unità da distribuire (limite calcolato da flusia-core, `max_orders`), né più di
 * 10 per volta. Controlli e scrittura in flusia-core (`/api/orders/deal-plan`).
 */

interface DealOrderSplitModalProps {
  dealId: string;
  organizationId: string;
  onClose: () => void;
  onDone: (result: DealOrderPlanResult) => void;
}

interface Column {
  key: string;
  /** Ordine in bozza esistente; assente = ordine nuovo */
  orderId?: string;
  orderNumber?: string;
}

const MAX_ORDERS = 10;
const EPS = 0.0005;

/** `unit` decide il punto delle migliaia (a pezzi niente punto) e viene mostrata secondo `unitDisplay` */
function formatQty(value: number, unit?: string | null, unitDisplay: QuantityFormatOptions['unitDisplay'] = 'suffix'): string {
  return formatQuantity(value, { unit, unitDisplay });
}

function parseQty(raw: string | undefined): number | null {
  const text = (raw ?? '').trim().replace(',', '.');
  if (text === '') return 0;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) / 1000 : null;
}

let newColumnSeq = 0;
const newColumn = (): Column => ({ key: `new-${++newColumnSeq}` });

export default function DealOrderSplitModal({ dealId, organizationId, onClose, onDone }: DealOrderSplitModalProps) {
  const { toast } = useApp();
  const [lines, setLines] = useState<DealOrderLine[]>([]);
  const [orders, setOrders] = useState<DealLinkedOrder[]>([]);
  const [maxOrders, setMaxOrders] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [columns, setColumns] = useState<Column[]>([]);
  /** column key -> deal_service_id -> quantità digitata */
  const [qty, setQty] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await loadDealOrderLines(dealId, organizationId);
        if (cancelled) return;
        setLines(data.lines);
        setOrders(data.orders);
        setMaxOrders(data.maxOrders);

        const drafts = data.orders.filter((o) => o.status === 'draft');
        const cols: Column[] = drafts.map((o) => ({ key: o.id, orderId: o.id, orderNumber: o.order_number }));
        const initial: Record<string, Record<string, string>> = {};
        drafts.forEach((o) => {
          initial[o.id] = {};
          data.lines.forEach((l) => {
            const q = l.by_order[o.id] ?? 0;
            if (q > 0) initial[o.id][l.deal_service_id] = String(q);
          });
        });
        if (cols.length === 0) {
          // Nessuna bozza: un ordine con tutto ciò che resta, da dividere se serve
          const first = newColumn();
          cols.push(first);
          initial[first.key] = {};
          data.lines.forEach((l) => {
            if (l.remaining > 0) initial[first.key][l.deal_service_id] = String(l.remaining);
          });
        }
        setColumns(cols);
        setQty(initial);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Righe dell'offerta non caricate");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, organizationId]);

  const draftIds = useMemo(() => new Set(orders.filter((o) => o.status === 'draft').map((o) => o.id)), [orders]);
  const lockedOrders = orders.filter((o) => o.status !== 'draft' && o.status !== 'cancelled');
  const existingCount = columns.filter((c) => c.orderId).length;
  const minColumns = Math.max(1, existingCount);
  // Ordini confermati contano nel limite: restano gli ordini dividibili qui
  const maxColumns = Math.max(minColumns, Math.min(MAX_ORDERS, maxOrders - lockedOrders.length));

  /** Quantità della riga già in ordini non modificabili qui */
  const lockedQty = (l: DealOrderLine) =>
    Object.entries(l.by_order).reduce((sum, [orderId, q]) => (draftIds.has(orderId) ? sum : sum + q), 0);

  const cell = (col: Column, l: DealOrderLine) => parseQty(qty[col.key]?.[l.deal_service_id]);

  const rows = lines.map((l) => {
    const available = Math.max(0, Math.round((l.quantity - lockedQty(l)) * 1000) / 1000);
    const values = columns.map((c) => cell(c, l));
    const invalid = values.some((v) => v === null);
    const assigned = values.reduce<number>((sum, v) => sum + (v ?? 0), 0);
    const unassigned = Math.round((available - assigned) * 1000) / 1000;
    return { line: l, available, invalid, unassigned, over: unassigned < -EPS };
  });

  const columnLabel = (col: Column, index: number) => {
    if (col.orderNumber) return col.orderNumber;
    const n = columns.slice(0, index + 1).filter((c) => !c.orderId).length;
    return existingCount > 0 ? `Nuovo ordine ${n}` : `Ordine ${n}`;
  };
  const columnTotal = (col: Column) =>
    lines.reduce((sum, l) => sum + (cell(col, l) ?? 0) * l.unit_price, 0);
  const columnEmpty = (col: Column) => lines.every((l) => (cell(col, l) ?? 0) <= EPS);

  const hasInvalid = rows.some((r) => r.invalid);
  const hasOver = rows.some((r) => r.over);
  const emptyExisting = columns.filter((c) => c.orderId && columnEmpty(c));
  const emptyNew = columns.filter((c) => !c.orderId && columnEmpty(c));
  const newToCreate = columns.filter((c) => !c.orderId && !columnEmpty(c)).length;
  const canSave = !saving && !hasInvalid && !hasOver && emptyExisting.length === 0
    && (newToCreate > 0 || existingCount > 0);

  const setColumnsCount = (count: number) => {
    const target = Math.min(maxColumns, Math.max(minColumns, count));
    setColumns((prev) => {
      if (target > prev.length) {
        const added = Array.from({ length: target - prev.length }, newColumn);
        return [...prev, ...added];
      }
      // Si tolgono solo ordini nuovi, dagli ultimi
      const next = [...prev];
      while (next.length > target) {
        const idx = next.map((c) => !c.orderId).lastIndexOf(true);
        if (idx < 0) break;
        next.splice(idx, 1);
      }
      return next;
    });
  };

  const setCell = (col: Column, l: DealOrderLine, value: string) =>
    setQty((prev) => ({ ...prev, [col.key]: { ...(prev[col.key] ?? {}), [l.deal_service_id]: value } }));

  /** Mette in questa colonna tutto ciò che non è ancora assegnato */
  const fillColumn = (col: Column) => {
    setQty((prev) => {
      const next = { ...prev, [col.key]: { ...(prev[col.key] ?? {}) } };
      rows.forEach((r) => {
        if (r.invalid || r.unassigned <= EPS) return;
        const current = cell(col, r.line) ?? 0;
        next[col.key][r.line.deal_service_id] = String(Math.round((current + r.unassigned) * 1000) / 1000);
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const plan = columns
        .filter((c) => c.orderId || !columnEmpty(c))
        .map((c) => ({
          ...(c.orderId ? { order_id: c.orderId } : {}),
          lines: lines
            .map((l) => ({ deal_service_id: l.deal_service_id, quantity: cell(c, l) ?? 0 }))
            .filter((x) => x.quantity > EPS),
        }));
      const result = await applyDealOrderPlan(dealId, organizationId, plan);
      toast.success(messaggioPiano(result));
      onDone(result);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Errore durante il salvataggio degli ordini');
    } finally {
      setSaving(false);
    }
  };

  const saveLabel = existingCount === 0
    ? (newToCreate === 1 ? 'Crea ordine' : `Crea ${newToCreate} ordini`)
    : 'Salva ordini';

  const inputClass = (bad: boolean) =>
    `w-20 px-2 py-1 text-sm text-right rounded-lg border bg-white dark:bg-gray-800 dark:text-white disabled:opacity-40 ${
      bad ? 'border-red-400 dark:border-red-500' : 'border-[rgba(55,53,47,0.16)] dark:border-gray-600'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={saving ? undefined : onClose} />
      <div className="relative w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden shadow-xl bg-white dark:bg-gray-900 border border-transparent dark:border-gray-700">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-[#37352F] dark:text-white">Ordini dall'offerta</h3>
            <p className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">
              Scegli in quanti ordini dividere l'offerta e quanto di ogni articolo va in ciascun ordine.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 rounded-lg text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#787774] dark:text-gray-400 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Caricamento righe dell'offerta...
            </div>
          ) : loadError ? (
            <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {loadError}
            </div>
          ) : lines.length === 0 ? (
            <p className="text-sm text-[#787774] dark:text-gray-400">L'offerta non ha righe prodotto.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-[#37352F] dark:text-gray-200">In quanti ordini?</span>
                <div className="flex items-center rounded-lg border border-[rgba(55,53,47,0.16)] dark:border-gray-600 overflow-hidden">
                  <button
                    onClick={() => setColumnsCount(columns.length - 1)}
                    disabled={columns.length <= minColumns}
                    className="px-2 py-1.5 text-[#37352F] dark:text-gray-200 hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-gray-800 disabled:opacity-30"
                    aria-label="Un ordine in meno"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-8 text-center text-sm font-medium text-[#37352F] dark:text-white">{columns.length}</span>
                  <button
                    onClick={() => setColumnsCount(columns.length + 1)}
                    disabled={columns.length >= maxColumns}
                    className="px-2 py-1.5 text-[#37352F] dark:text-gray-200 hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-gray-800 disabled:opacity-30"
                    aria-label="Un ordine in più"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="text-xs text-[#787774] dark:text-gray-400">
                  {maxColumns === 1
                    ? "Non c'è abbastanza da dividere: un solo ordine."
                    : `Al massimo ${maxColumns}${maxOrders - lockedOrders.length > MAX_ORDERS ? ' per volta' : ' (uno per ogni unità da distribuire)'}.`}
                </span>
                {lockedOrders.length > 0 && (
                  <span className="text-xs text-[#787774] dark:text-gray-400">
                    Già confermati, non modificabili qui: {lockedOrders.map((o) => o.order_number).join(', ')}
                  </span>
                )}
              </div>

              <div className="rounded-xl border border-[rgba(55,53,47,0.12)] dark:border-gray-700 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[rgba(55,53,47,0.03)] dark:bg-gray-800/50 text-xs text-[#787774] dark:text-gray-400">
                      <th className="text-left font-medium px-3 py-2 min-w-[200px]">Articolo</th>
                      {columns.map((c, i) => (
                        <th key={c.key} className="text-right font-medium px-3 py-2 whitespace-nowrap">
                          <div className="text-[#37352F] dark:text-gray-200">{columnLabel(c, i)}</div>
                          <button
                            onClick={() => fillColumn(c)}
                            className="text-[11px] font-normal text-[#2383E2] hover:underline"
                          >
                            Metti qui il resto
                          </button>
                        </th>
                      ))}
                      <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Da assegnare</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(55,53,47,0.09)] dark:divide-gray-700">
                    {rows.map((r) => {
                      const l = r.line;
                      // Unità della riga (vuota = pz): mostrata solo se l'offerta la indica
                      const um = resolveProductUnit({ unit_of_measure_1: l.unit_of_measure });
                      const umDisplay = l.unit_of_measure ? 'suffix' : 'none';
                      const locked = Math.round(lockedQty(l) * 1000) / 1000;
                      const disabled = r.available <= 0 && columns.every((c) => (cell(c, l) ?? 0) <= EPS);
                      return (
                        <tr key={l.deal_service_id} className={disabled ? 'opacity-60' : ''}>
                          <td className="px-3 py-2 align-top">
                            <div className="font-medium text-[#37352F] dark:text-white">{l.product_name}</div>
                            <div className="text-xs text-[#787774] dark:text-gray-400">
                              {l.product_sku ? `${l.product_sku} · ` : ''}
                              Offerta {formatQty(l.quantity, um, umDisplay)} · {formatCurrency(l.unit_price)}
                            </div>
                            {locked > 0 && (
                              <div className="text-[11px] text-[#9B9A97] dark:text-gray-500">
                                In ordini confermati: {formatQty(locked, um, umDisplay)}
                              </div>
                            )}
                          </td>
                          {columns.map((c) => {
                            const value = qty[c.key]?.[l.deal_service_id] ?? '';
                            const bad = parseQty(value) === null || r.over;
                            return (
                              <td key={c.key} className="px-3 py-2 text-right align-top">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={value}
                                  placeholder="0"
                                  disabled={disabled}
                                  onChange={(e) => setCell(c, l, e.target.value)}
                                  className={inputClass(bad)}
                                  aria-label={`Quantità di ${l.product_name} in ${c.orderNumber ?? 'ordine'}`}
                                />
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-right align-top whitespace-nowrap">
                            <span className={
                              r.over
                                ? 'text-red-600 dark:text-red-400 font-medium'
                                : r.unassigned > EPS
                                  ? 'text-amber-700 dark:text-amber-400'
                                  : 'text-[#0F7B6C] dark:text-emerald-400'
                            }>
                              {r.invalid ? '—' : formatQty(r.unassigned, um, l.unit_of_measure ? 'parens' : 'none')}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[rgba(55,53,47,0.03)] dark:bg-gray-800/50 text-xs text-[#787774] dark:text-gray-400">
                      <td className="px-3 py-2">Totale imponibile</td>
                      {columns.map((c) => (
                        <td key={c.key} className="px-3 py-2 text-right font-medium text-[#37352F] dark:text-gray-200 whitespace-nowrap">
                          {formatCurrency(columnTotal(c))}
                        </td>
                      ))}
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <p className="text-xs text-[#787774] dark:text-gray-400">
                Le quantità non assegnate restano da ordinare e si possono mettere in un ordine più avanti.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && !loadError && lines.length > 0 && (
          <div className="px-5 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 space-y-2">
            {saveError && (
              <div className="flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                {saveError}
              </div>
            )}
            {hasInvalid && (
              <div className="text-xs text-red-600 dark:text-red-400">Alcune quantità non sono numeri validi.</div>
            )}
            {hasOver && (
              <div className="text-xs text-red-600 dark:text-red-400">
                Per alcuni articoli le quantità assegnate superano quelle dell'offerta.
              </div>
            )}
            {emptyExisting.length > 0 && (
              <div className="text-xs text-red-600 dark:text-red-400">
                {emptyExisting.map((c) => c.orderNumber).join(', ')} resterebbe senza righe: assegnagli almeno un articolo oppure eliminalo dal dettaglio ordine.
              </div>
            )}
            {emptyNew.length > 0 && !hasInvalid && (
              <div className="text-xs text-amber-700 dark:text-amber-400">
                {emptyNew.length === 1 ? 'Un ordine nuovo è vuoto e non verrà creato.' : `${emptyNew.length} ordini nuovi sono vuoti e non verranno creati.`}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-3 py-1.5 text-xs rounded-lg text-[#37352F] dark:text-gray-200 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#0F7B6C] text-white hover:bg-[#0a6257] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackagePlus className="w-3.5 h-3.5" />}
                {saveLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
